import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  CheckCircleOutline as ApproveIcon,
  CancelOutlined as RejectIcon,
  RefreshOutlined as RefreshIcon,
  TrendingUp as TrendUpIcon,
  TrendingDown as TrendDownIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchClients } from '@src/api/actions/clients';
import {
  fetchOptimizerRuns,
  approveOptimizerRun,
  rejectOptimizerRun,
  fetchOptimizerConfigs,
  createOptimizerConfig,
  updateOptimizerConfig,
  triggerOptimizerRun,
} from '@src/api/actions';

// ── Formatters ──────────────────────────────────────────────────

const fmtUSD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fmtCents = (cents) => fmtUSD.format((cents || 0) / 100);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-');

// ── Stat Card ───────────────────────────────────────────────────

function StatCard({ label, value, sub, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 140 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ mt: 0.5, color: color || 'text.primary' }}>{value}</Typography>
      {sub && <Typography variant="body2" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

// ── Status Chip ─────────────────────────────────────────────────

function StatusChip({ status, type = 'approval' }) {
  const colorMap = type === 'approval'
    ? { draft: 'warning', approved: 'success', rejected: 'error' }
    : { not_applied: 'default', applied: 'success', failed: 'error' };
  const labelMap = type === 'approval'
    ? { draft: 'Pending', approved: 'Approved', rejected: 'Rejected' }
    : { not_applied: 'Not Applied', applied: 'Applied', failed: 'Failed' };
  return (
    <Chip
      label={labelMap[status] || status}
      color={colorMap[status] || 'default'}
      size="small"
      variant={type === 'apply' ? 'outlined' : 'filled'}
    />
  );
}

// ── Optimizer Runs Tab ──────────────────────────────────────────

function OptimizerRunsTab({ clients }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterClientId, setFilterClientId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [approveRun, setApproveRun] = useState(null);
  const [rejectRun, setRejectRun] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [triggerClientId, setTriggerClientId] = useState('');
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [triggerLog, setTriggerLog] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (filterClientId) params.client_id = filterClientId;
    if (filterStatus) params.approval_status = filterStatus;
    fetchOptimizerRuns(params)
      .then((res) => setRuns(res.data?.results || res.data || []))
      .catch((err) => setError(err?.message || 'Failed to load optimizer runs.'))
      .finally(() => setLoading(false));
  }, [filterClientId, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = () => {
    if (!approveRun) return;
    setActionLoading(true);
    setActionError('');
    approveOptimizerRun(approveRun.id)
      .then(() => { setApproveRun(null); load(); })
      .catch((err) => setActionError(err?.response?.data?.detail || err?.message || 'Approval failed.'))
      .finally(() => setActionLoading(false));
  };

  const handleReject = () => {
    if (!rejectRun) return;
    setActionLoading(true);
    setActionError('');
    rejectOptimizerRun(rejectRun.id, rejectNotes)
      .then(() => { setRejectRun(null); setRejectNotes(''); load(); })
      .catch((err) => setActionError(err?.response?.data?.detail || err?.message || 'Rejection failed.'))
      .finally(() => setActionLoading(false));
  };

  // Summary stats
  const stats = useMemo(() => {
    const pending = runs.filter((r) => r.approval_status === 'draft').length;
    const applied = runs.filter((r) => r.apply_status === 'applied').length;
    const totalChanges = runs.reduce((sum, r) => sum + (r.summary_snapshot?.budget_change_count || 0), 0);
    return { pending, applied, totalChanges, total: runs.length };
  }, [runs]);

  return (
    <>
      {/* Summary cards */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <StatCard label="Total Runs" value={stats.total} />
        <StatCard label="Pending Approval" value={stats.pending} color={stats.pending > 0 ? 'warning.main' : undefined} />
        <StatCard label="Applied" value={stats.applied} color="success.main" />
        <StatCard label="Total Changes" value={stats.totalChanges} />
      </Stack>

      {/* Filters + Table */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {actionError && <Alert severity="error">{actionError}</Alert>}

            <Stack direction="row" spacing={2} alignItems="center">
              <Autocomplete
                options={clients}
                getOptionLabel={(opt) => opt.name || ''}
                value={clients.find((c) => c.id === Number(filterClientId)) || null}
                onChange={(_, val) => setFilterClientId(val?.id || '')}
                sx={{ minWidth: 250 }}
                renderInput={(params) => <TextField {...params} label="Filter by Client" size="small" />}
              />
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel size="small">Status</InputLabel>
                <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)} size="small">
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="draft">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              <Button startIcon={<RefreshIcon />} variant="outlined" size="small" onClick={load} disabled={loading}>
                Refresh
              </Button>
              <Divider orientation="vertical" flexItem />
              <Autocomplete
                options={clients}
                getOptionLabel={(opt) => opt.name || ''}
                value={clients.find((c) => c.id === Number(triggerClientId)) || null}
                onChange={(_, val) => setTriggerClientId(val?.id || '')}
                sx={{ minWidth: 200 }}
                renderInput={(params) => <TextField {...params} label="Run for Client" size="small" />}
              />
              <Button
                variant="contained"
                size="small"
                disabled={!triggerClientId || triggerLoading}
                onClick={() => {
                  setTriggerLoading(true);
                  setTriggerLog(null);
                  setActionError('');
                  triggerOptimizerRun(triggerClientId)
                    .then((res) => {
                      setTriggerLog(res.data?.log || []);
                      load();
                    })
                    .catch((err) => {
                      const data = err?.response?.data;
                      setTriggerLog(data?.log || []);
                      setActionError(data?.detail || err?.message || 'Run failed.');
                    })
                    .finally(() => setTriggerLoading(false));
                }}
              >
                {triggerLoading ? <CircularProgress size={18} /> : 'Run Now (Dry Run)'}
              </Button>
            </Stack>

            {/* Step-by-step log panel */}
            {triggerLog && (
              <Card variant="outlined" sx={{ bgcolor: 'grey.900', maxHeight: 500, overflow: 'auto' }}>
                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" sx={{ color: 'grey.400', mb: 1, display: 'block' }}>
                    Optimizer Run Log ({triggerLog.length} steps)
                    <Button size="small" sx={{ ml: 2, color: 'grey.500' }} onClick={() => setTriggerLog(null)}>
                      Close
                    </Button>
                  </Typography>
                  {triggerLog.map((entry, i) => {
                    const stepColors = {
                      start: '#64b5f6', complete: '#81c784', campaign_enriched: '#ce93d8',
                      budget_change: '#ffb74d', value_rule: '#4fc3f7', pause_flag: '#ef5350',
                      client_funnel: '#a5d6a7', deal_stability: '#fff176', whatif: '#90caf9',
                      campaigns_loaded: '#80cbc4', pause_flags: '#e0e0e0', no_campaigns: '#ef9a9a',
                    };
                    const color = stepColors[entry.step] || '#e0e0e0';
                    return (
                      <Box key={i} sx={{ py: 0.3, fontFamily: 'monospace', fontSize: 12 }}>
                        <Typography component="span" sx={{ color, fontWeight: 600, fontSize: 12, fontFamily: 'monospace' }}>
                          [{entry.step}]
                        </Typography>
                        <Typography component="span" sx={{ color: '#e0e0e0', ml: 1, fontSize: 12, fontFamily: 'monospace' }}>
                          {entry.detail}
                        </Typography>
                        {entry.data && (
                          <Box sx={{ ml: 3, mt: 0.3 }}>
                            {typeof entry.data === 'object' && !Array.isArray(entry.data) ? (
                              Object.entries(entry.data).map(([k, v]) => {
                                if (k === 'campaigns' || k === 'why_drivers') return null;
                                const display = typeof v === 'object' ? JSON.stringify(v) : String(v ?? '');
                                if (!display || display === 'null' || display === '{}') return null;
                                return (
                                  <Typography key={k} sx={{ color: '#9e9e9e', fontSize: 11, fontFamily: 'monospace' }}>
                                    {k}: {display}
                                  </Typography>
                                );
                              })
                            ) : (
                              <Typography sx={{ color: '#9e9e9e', fontSize: 11, fontFamily: 'monospace' }}>
                                {JSON.stringify(entry.data)}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : runs.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No optimizer runs found. Runs are created daily when clients have optimizer configs enabled.</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40 }} />
                      <TableCell>Client</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Budget Changes</TableCell>
                      <TableCell align="center">Value Rules</TableCell>
                      <TableCell align="right">Current Daily</TableCell>
                      <TableCell align="right">Recommended</TableCell>
                      <TableCell align="right">Delta</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Applied</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runs.map((run) => {
                      const s = run.summary_snapshot || {};
                      const isOpen = expandedId === run.id;
                      const snapshots = run.campaign_budget_snapshot || [];
                      const currentCents = s.current_daily_budget_total_cents || 0;
                      const recCents = s.recommended_daily_budget_total_cents || 0;
                      const deltaCents = recCents - currentCents;

                      return (
                        <Box component="tbody" key={run.id}>
                          <TableRow hover sx={{ '& > *': { borderBottom: isOpen ? 'none' : undefined } }}>
                            <TableCell>
                              <IconButton size="small" onClick={() => setExpandedId(isOpen ? null : run.id)}>
                                {isOpen ? <CollapseIcon /> : <ExpandIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{run.client_name}</Typography>
                            </TableCell>
                            <TableCell>{fmtTime(run.created_at)}</TableCell>
                            <TableCell align="center">{s.budget_change_count || 0}</TableCell>
                            <TableCell align="center">{s.value_rule_change_count || 0}</TableCell>
                            <TableCell align="right">{fmtCents(currentCents)}</TableCell>
                            <TableCell align="right">{fmtCents(recCents)}</TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                {deltaCents > 0 ? <TrendUpIcon fontSize="small" color="success" /> : deltaCents < 0 ? <TrendDownIcon fontSize="small" color="error" /> : null}
                                <Typography
                                  variant="body2"
                                  sx={{ color: deltaCents > 0 ? 'success.main' : deltaCents < 0 ? 'error.main' : 'text.secondary' }}
                                >
                                  {deltaCents > 0 ? '+' : ''}{fmtCents(deltaCents)}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="center"><StatusChip status={run.approval_status} type="approval" /></TableCell>
                            <TableCell align="center"><StatusChip status={run.apply_status} type="apply" /></TableCell>
                            <TableCell align="right">
                              {run.approval_status === 'draft' && (
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <Button size="small" variant="contained" color="success" startIcon={<ApproveIcon />} onClick={() => setApproveRun(run)}>
                                    Approve
                                  </Button>
                                  <Button size="small" variant="outlined" color="error" startIcon={<RejectIcon />} onClick={() => setRejectRun(run)}>
                                    Reject
                                  </Button>
                                </Stack>
                              )}
                            </TableCell>
                          </TableRow>

                          {/* Expanded detail */}
                          <TableRow>
                            <TableCell colSpan={11} sx={{ py: 0, bgcolor: 'action.hover' }}>
                              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 2, px: 1 }}>
                                  <Stack spacing={2}>
                                    {/* Run context */}
                                    <Stack direction="row" spacing={2} flexWrap="wrap">
                                      <Chip label={`Mode: ${s.mode || 'N/A'}`} size="small" variant="outlined" />
                                      <Chip label={`Day ${s.days_elapsed || 0} of ${(s.days_elapsed || 0) + (s.days_remaining || 0)}`} size="small" variant="outlined" />
                                      <Chip label={`MTD: ${fmtUSD.format(Number(s.total_mtd_spend || 0))}`} size="small" variant="outlined" />
                                      {s.deal_stability && <Chip label={`Deals: ${s.deal_stability}`} size="small" variant="outlined" color={s.deal_stability === 'stable' ? 'success' : s.deal_stability === 'volatile' ? 'error' : 'warning'} />}
                                      {s.dow_active && <Chip label="DOW Active" size="small" color="info" />}
                                      {s.pause_flags > 0 && <Chip label={`${s.pause_flags} Pause Flag${s.pause_flags > 1 ? 's' : ''}`} size="small" color="error" />}
                                    </Stack>

                                    {/* What-if comparison */}
                                    {run.whatif_snapshot?.yesterday_date && (
                                      <Alert severity="info" variant="outlined">
                                        <strong>What-If ({run.whatif_snapshot.yesterday_date}):</strong>{' '}
                                        Yesterday recommended {fmtUSD.format(run.whatif_snapshot.recommended_daily_total || 0)}/day,
                                        actual was {fmtUSD.format(run.whatif_snapshot.actual_daily_total || 0)}/day,
                                        spent {fmtUSD.format(run.whatif_snapshot.actual_spend || 0)}.
                                        {run.whatif_snapshot.recommendation_was_applied ? ' (Applied)' : ' (Not applied)'}
                                      </Alert>
                                    )}

                                    <Typography variant="subtitle2">Campaign Budget Adjustments</Typography>
                                    {snapshots.length > 0 ? (
                                      <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                              <TableCell sx={{ minWidth: 350 }}>Campaign</TableCell>
                                              <TableCell>Type</TableCell>
                                              <TableCell align="center">Stage</TableCell>
                                              <TableCell align="right">COM</TableCell>
                                              <TableCell align="right">Current</TableCell>
                                              <TableCell align="right">Recommended</TableCell>
                                              <TableCell align="right">Change</TableCell>
                                              <TableCell sx={{ minWidth: 280 }}>Why</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {snapshots.map((cs, idx) => {
                                              const oldB = (cs.current_daily_budget_cents || 0) / 100;
                                              const newB = (cs.recommended_daily_budget_cents || 0) / 100;
                                              const d = newB - oldB;
                                              const pct = oldB > 0 ? ((d / oldB) * 100).toFixed(1) : '0.0';
                                              const stageColor = cs.maturity_stage === 'mature' ? 'success' : cs.maturity_stage === 'developing' ? 'warning' : 'default';
                                              const comVal = cs.effective_com ? `${(Number(cs.effective_com) * 100).toFixed(0)}%` : '-';
                                              return (
                                                <>
                                                  <TableRow key={idx} hover>
                                                    <TableCell>
                                                      <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography
                                                          variant="body2"
                                                          component="a"
                                                          href={cs.campaign_id ? `https://www.facebook.com/adsmanager/manage/campaigns?act=&campaign_ids=${cs.campaign_id}` : undefined}
                                                          target="_blank"
                                                          rel="noopener"
                                                          sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                                        >
                                                          {cs.campaign_name || cs.campaign_id}
                                                        </Typography>
                                                        {cs.pause_flag && <Chip label="PAUSE" size="small" color="error" />}
                                                      </Stack>
                                                    </TableCell>
                                                    <TableCell><Chip label={cs.campaign_type || 'Broad'} size="small" variant="outlined" /></TableCell>
                                                    <TableCell align="center">
                                                      <Chip label={`${cs.maturity_stage || '?'} (${cs.com_confidence || '?'})`} size="small" color={stageColor} variant="outlined" />
                                                    </TableCell>
                                                    <TableCell align="right">{comVal}</TableCell>
                                                    <TableCell align="right">{fmtUSD.format(oldB)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtUSD.format(newB)}</TableCell>
                                                    <TableCell align="right">
                                                      <Typography variant="body2" sx={{ color: d > 0 ? 'success.main' : d < 0 ? 'error.main' : 'text.secondary', fontWeight: 500 }}>
                                                        {d > 0 ? '+' : ''}{fmtUSD.format(d)} ({d > 0 ? '+' : ''}{pct}%)
                                                      </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ maxWidth: 350 }}>
                                                        {(cs.why_drivers || []).map((driver, di) => (
                                                          <Chip key={di} label={driver} size="small" variant="outlined" color={driver.includes('better') ? 'success' : driver.includes('worse') || driver.includes('PAUSE') ? 'error' : 'default'} sx={{ mb: 0.5 }} />
                                                        ))}
                                                      </Stack>
                                                    </TableCell>
                                                  </TableRow>
                                                  {cs.pause_flag && cs.pause_reason && (
                                                    <TableRow key={`${idx}-pause`}>
                                                      <TableCell colSpan={8} sx={{ py: 0.5, bgcolor: 'error.50' }}>
                                                        <Typography variant="caption" color="error.main">
                                                          {cs.pause_reason}{cs.redistribute_to ? ` — Redistribute to: ${cs.redistribute_to}` : ''}
                                                        </Typography>
                                                      </TableCell>
                                                    </TableRow>
                                                  )}
                                                </>
                                              );
                                            })}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">No campaign budget changes in this run.</Typography>
                                    )}

                                    {/* Value Rule Adjustments */}
                                    {(run.city_execution_rules_snapshot || []).length > 0 && (
                                      <>
                                        <Typography variant="subtitle2" sx={{ mt: 1 }}>Value Rule Bid Adjustments</Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                <TableCell>Ad Set</TableCell>
                                                <TableCell>Rule</TableCell>
                                                <TableCell align="right">Bid Adjustment</TableCell>
                                                <TableCell align="right">Cities</TableCell>
                                                <TableCell sx={{ minWidth: 350 }}>City Names</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {(run.city_execution_rules_snapshot || []).map((vr, vi) => (
                                                <TableRow key={vi} hover>
                                                  <TableCell>
                                                    <Typography
                                                      variant="body2"
                                                      component="a"
                                                      href={vr.ad_set_id ? `https://www.facebook.com/adsmanager/manage/adsets?act=&adset_ids=${vr.ad_set_id}` : undefined}
                                                      target="_blank"
                                                      rel="noopener"
                                                      sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                                    >
                                                      {vr.ad_set_id}
                                                    </Typography>
                                                  </TableCell>
                                                  <TableCell>{vr.name}</TableCell>
                                                  <TableCell align="right">
                                                    <Chip
                                                      label={`${vr.adjustment_pct > 0 ? '+' : ''}${vr.adjustment_pct}%`}
                                                      size="small"
                                                      color={vr.adjustment_pct > 0 ? 'success' : 'error'}
                                                      variant="outlined"
                                                    />
                                                  </TableCell>
                                                  <TableCell align="right">{vr.city_count}</TableCell>
                                                  <TableCell>
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                                      {(vr.city_names || []).join(', ') || '-'}
                                                    </Typography>
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </>
                                    )}

                                    {run.apply_error && <Alert severity="error" variant="outlined">Apply Error: {run.apply_error}</Alert>}
                                  </Stack>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Box>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={Boolean(approveRun)} onClose={() => setApproveRun(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve & Apply to Facebook</DialogTitle>
        <DialogContent>
          {approveRun && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning" variant="outlined">
                This will push changes to Facebook Ads Manager for <strong>{approveRun.client_name}</strong>.
              </Alert>
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Budget Changes</Typography>
                  <Typography variant="h6">{approveRun.summary_snapshot?.budget_change_count || 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Value Rule Sets</Typography>
                  <Typography variant="h6">{approveRun.summary_snapshot?.value_rule_change_count || 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Daily Budget</Typography>
                  <Typography variant="h6">
                    {fmtCents(approveRun.summary_snapshot?.current_daily_budget_total_cents)} {' -> '}
                    {fmtCents(approveRun.summary_snapshot?.recommended_daily_budget_total_cents)}
                  </Typography>
                </Box>
              </Stack>
              {actionError && <Alert severity="error">{actionError}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveRun(null)} disabled={actionLoading}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Confirm & Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={Boolean(rejectRun)} onClose={() => setRejectRun(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Optimizer Run</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography>Reject this run for <strong>{rejectRun?.client_name}</strong>? No changes will be applied.</Typography>
            <TextField
              label="Notes (optional)"
              multiline
              rows={2}
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectRun(null); setRejectNotes(''); }} disabled={actionLoading}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Settings Tab ────────────────────────────────────────────────

function OptimizerSettingsTab({ clients }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [editConfig, setEditConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState({ type: '', text: '' });
  const [clientCampaigns, setClientCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [overrideCampaignId, setOverrideCampaignId] = useState('');
  const [overrideMin, setOverrideMin] = useState('');
  const [overrideMax, setOverrideMax] = useState('');

  const loadConfigs = useCallback(() => {
    setLoading(true);
    fetchOptimizerConfigs()
      .then((res) => setConfigs(res.data?.results || res.data || []))
      .catch((err) => setError(err?.message || 'Failed'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  useEffect(() => {
    if (!selectedClientId) { setEditConfig(null); return; }
    const existing = configs.find((c) => c.client === Number(selectedClientId));
    setEditConfig(existing
      ? { ...existing }
      : {
          client: Number(selectedClientId),
          enabled: false,
          mode: 'dry_run',
          territory_adherence_pct: 30,
          com_priority_weight: '0.60',
          broad_local_split_broad_pct: 85,
          min_daily_budget_cents: 500,
          max_daily_budget_cents: 100000,
          max_daily_change_pct: 30,
          com_lookback_days: 30,
          dow_enabled: false,
          dow_min_data_days: 60,
          dow_max_adjustment_pct: 20,
          pause_flag_enabled: true,
          pause_flag_threshold_multiplier: '3.0',
          campaign_budget_overrides: {},
        }
    );
    setSaveMsg({ type: '', text: '' });
  }, [selectedClientId, configs]);

  // Load campaigns for override selection
  useEffect(() => {
    if (!selectedClientId) { setClientCampaigns([]); return; }
    setCampaignsLoading(true);
    import('@src/api/clients/default').then(({ default: client }) => {
      client.request({
        url: `dashboard/facebook/ad-structure-db/?client_id=${selectedClientId}`,
        method: 'GET',
      })
        .then((res) => {
          const accounts = res.data || [];
          const campaigns = [];
          for (const acct of accounts) {
            for (const camp of (acct.campaigns || [])) {
              campaigns.push({ id: camp.campaign_id, name: camp.campaign_name || camp.campaign_id });
            }
          }
          setClientCampaigns(campaigns);
        })
        .catch(() => setClientCampaigns([]))
        .finally(() => setCampaignsLoading(false));
    });
  }, [selectedClientId]);

  const handleSave = () => {
    if (!editConfig) return;
    setSaving(true);
    setSaveMsg({ type: '', text: '' });
    const action = editConfig.id
      ? updateOptimizerConfig(editConfig.id, editConfig)
      : createOptimizerConfig(editConfig);
    action
      .then(() => { setSaveMsg({ type: 'success', text: 'Configuration saved successfully.' }); loadConfigs(); })
      .catch((err) => setSaveMsg({ type: 'error', text: err?.response?.data?.detail || JSON.stringify(err?.response?.data) || err?.message || 'Save failed.' }))
      .finally(() => setSaving(false));
  };

  const set = (field, value) => setEditConfig((prev) => ({ ...prev, [field]: value }));

  const modeDescriptions = {
    dry_run: 'Logs recommended changes but does not touch Facebook. Use this to validate the algorithm.',
    manual_approve: 'Creates runs that require manual approval before applying to Facebook.',
    auto_apply: 'Fully automated. Changes are applied to Facebook daily without approval.',
  };

  return (
    <>
      {/* Config summary table */}
      <Card>
        <CardHeader
          title="Configured Clients"
          subheader={`${configs.filter((c) => c.enabled).length} of ${configs.length} clients enabled`}
          action={loading ? <CircularProgress size={20} /> : null}
        />
        {error && <Alert severity="error" sx={{ mx: 2 }}>{error}</Alert>}
        {configs.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell align="center">Enabled</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell align="center">Territory Adherence</TableCell>
                  <TableCell align="center">COM Weight</TableCell>
                  <TableCell align="center">Broad / Local</TableCell>
                  <TableCell align="center">Max Change</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.map((cfg) => (
                  <TableRow key={cfg.id} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>{cfg.client_name}</Typography></TableCell>
                    <TableCell align="center">
                      <Chip label={cfg.enabled ? 'Active' : 'Off'} color={cfg.enabled ? 'success' : 'default'} size="small" variant={cfg.enabled ? 'filled' : 'outlined'} />
                    </TableCell>
                    <TableCell>
                      <Chip label={cfg.mode.replace('_', ' ')} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">{cfg.territory_adherence_pct}%</TableCell>
                    <TableCell align="center">{Math.round(Number(cfg.com_priority_weight) * 100)}%</TableCell>
                    <TableCell align="center">{cfg.broad_local_split_broad_pct} / {100 - cfg.broad_local_split_broad_pct}</TableCell>
                    <TableCell align="center">{cfg.max_daily_change_pct}%</TableCell>
                    <TableCell>
                      <Button size="small" startIcon={<SettingsIcon />} onClick={() => setSelectedClientId(String(cfg.client))}>
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Editor */}
      <Card>
        <CardHeader
          title={editConfig?.id ? `Edit: ${clients.find((c) => c.id === Number(selectedClientId))?.name || ''}` : 'Configure Client'}
          subheader="Select a client to configure their optimizer settings"
        />
        <Divider />
        <CardContent>
          <Stack spacing={3} sx={{ maxWidth: 640 }}>
            {saveMsg.text && <Alert severity={saveMsg.type}>{saveMsg.text}</Alert>}

            <Autocomplete
              options={clients}
              getOptionLabel={(opt) => opt.name || ''}
              value={clients.find((c) => c.id === Number(selectedClientId)) || null}
              onChange={(_, val) => setSelectedClientId(val?.id ? String(val.id) : '')}
              renderInput={(params) => <TextField {...params} label="Client" />}
            />

            {editConfig && (
              <>
                <Divider />

                <FormControlLabel
                  control={<Switch checked={editConfig.enabled} onChange={(e) => set('enabled', e.target.checked)} color="success" />}
                  label={
                    <Stack>
                      <Typography variant="body1">Optimizer Enabled</Typography>
                      <Typography variant="caption" color="text.secondary">When enabled, the optimizer will generate daily runs for this client.</Typography>
                    </Stack>
                  }
                />

                <FormControl fullWidth>
                  <InputLabel>Mode</InputLabel>
                  <Select value={editConfig.mode} label="Mode" onChange={(e) => set('mode', e.target.value)}>
                    <MenuItem value="dry_run">Dry Run</MenuItem>
                    <MenuItem value="manual_approve">Manual Approve</MenuItem>
                    <MenuItem value="auto_apply">Auto Apply</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
                  {modeDescriptions[editConfig.mode]}
                </Typography>

                <Divider />
                <Typography variant="subtitle2">Budget Allocation</Typography>

                <Box>
                  <Typography variant="body2" gutterBottom>
                    Broad / Local Split: <strong>{editConfig.broad_local_split_broad_pct}%</strong> Broad, <strong>{100 - editConfig.broad_local_split_broad_pct}%</strong> Local
                  </Typography>
                  <Slider
                    value={editConfig.broad_local_split_broad_pct}
                    onChange={(_, v) => set('broad_local_split_broad_pct', v)}
                    min={50} max={100} step={5}
                    marks={[{ value: 50, label: '50/50' }, { value: 85, label: '85/15' }, { value: 100, label: '100/0' }]}
                  />
                </Box>

                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Min Daily Budget"
                    type="number"
                    size="small"
                    value={Math.round(editConfig.min_daily_budget_cents / 100)}
                    onChange={(e) => set('min_daily_budget_cents', Math.round(Number(e.target.value) * 100))}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Max Daily Budget"
                    type="number"
                    size="small"
                    value={Math.round(editConfig.max_daily_budget_cents / 100)}
                    onChange={(e) => set('max_daily_budget_cents', Math.round(Number(e.target.value) * 100))}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                    sx={{ flex: 1 }}
                  />
                </Stack>

                <Divider />
                <Typography variant="subtitle2">Optimization Strategy</Typography>

                <Box>
                  <Typography variant="body2" gutterBottom>
                    Territory Adherence Threshold: <strong>{editConfig.territory_adherence_pct}%</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    How far territory spend can deviate from budget before the optimizer aggressively corrects.
                  </Typography>
                  <Slider
                    value={editConfig.territory_adherence_pct}
                    onChange={(_, v) => set('territory_adherence_pct', v)}
                    min={5} max={50} step={5}
                    marks={[{ value: 10, label: 'Strict' }, { value: 30, label: 'Default' }, { value: 50, label: 'Flexible' }]}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" gutterBottom>
                    Optimization Priority: <strong>{Math.round(Number(editConfig.com_priority_weight) * 100)}%</strong> Cost of Marketing / <strong>{100 - Math.round(Number(editConfig.com_priority_weight) * 100)}%</strong> Budget Adherence
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Higher COM weight means the optimizer prioritizes placing spend where cost of marketing is best, even if it deviates from territory budgets.
                  </Typography>
                  <Slider
                    value={Math.round(Number(editConfig.com_priority_weight) * 100)}
                    onChange={(_, v) => set('com_priority_weight', (v / 100).toFixed(2))}
                    min={0} max={100} step={5}
                    marks={[{ value: 0, label: 'Budget' }, { value: 60, label: 'Default' }, { value: 100, label: 'COM' }]}
                  />
                </Box>

                <Divider />
                <Typography variant="subtitle2">Guard Rails</Typography>

                <Box>
                  <Typography variant="body2" gutterBottom>
                    Base Change Cap: <strong>{editConfig.max_daily_change_pct}%</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Base % a campaign budget can change per run. Automatically scales: lower early in the month (15%), this value mid-month, higher late (40%). Increases further when pacing is off target.
                  </Typography>
                  <Slider
                    value={editConfig.max_daily_change_pct}
                    onChange={(_, v) => set('max_daily_change_pct', v)}
                    min={5} max={50} step={5}
                    marks={[{ value: 10, label: 'Conservative' }, { value: 30, label: 'Default' }, { value: 50, label: 'Aggressive' }]}
                  />
                </Box>

                <Divider />
                <Typography variant="subtitle2">Day-of-Week Optimization</Typography>

                <FormControlLabel
                  control={<Switch checked={editConfig.dow_enabled || false} onChange={(e) => set('dow_enabled', e.target.checked)} />}
                  label={
                    <Stack>
                      <Typography variant="body1">Day-of-Week Adjustments</Typography>
                      <Typography variant="caption" color="text.secondary">Adjust budgets based on which days convert better. Requires 60+ days of data.</Typography>
                    </Stack>
                  }
                />

                {editConfig.dow_enabled && (
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Min Data Days"
                      type="number"
                      size="small"
                      value={editConfig.dow_min_data_days || 60}
                      onChange={(e) => set('dow_min_data_days', Number(e.target.value))}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Max DOW Adjustment %"
                      type="number"
                      size="small"
                      value={editConfig.dow_max_adjustment_pct || 20}
                      onChange={(e) => set('dow_max_adjustment_pct', Number(e.target.value))}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                )}

                <Divider />
                <Typography variant="subtitle2">Pause Flagging</Typography>

                <FormControlLabel
                  control={<Switch checked={editConfig.pause_flag_enabled !== false} onChange={(e) => set('pause_flag_enabled', e.target.checked)} />}
                  label={
                    <Stack>
                      <Typography variant="body1">Flag Campaigns for Pause</Typography>
                      <Typography variant="caption" color="text.secondary">Flag campaigns with significantly worse COM than peers. Never auto-pauses — you decide.</Typography>
                    </Stack>
                  }
                />

                {editConfig.pause_flag_enabled !== false && (
                  <TextField
                    label="Pause Threshold (x worse than median)"
                    type="number"
                    size="small"
                    value={editConfig.pause_flag_threshold_multiplier || 3.0}
                    onChange={(e) => set('pause_flag_threshold_multiplier', e.target.value)}
                    inputProps={{ step: 0.5, min: 1.5, max: 10 }}
                    sx={{ maxWidth: 300 }}
                    helperText="COM must be this many times worse than vertical median"
                  />
                )}

                <Divider />
                <Typography variant="subtitle2">Campaign Budget Overrides</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Set per-campaign min/max daily budgets. Overrides the global min/max for specific campaigns.
                </Typography>

                {/* Existing overrides */}
                {Object.keys(editConfig.campaign_budget_overrides || {}).length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell>Campaign</TableCell>
                          <TableCell align="right">Min Daily</TableCell>
                          <TableCell align="right">Max Daily</TableCell>
                          <TableCell align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(editConfig.campaign_budget_overrides || {}).map(([campId, bounds]) => {
                          const camp = clientCampaigns.find((c) => c.id === campId);
                          return (
                            <TableRow key={campId}>
                              <TableCell>
                                <Typography variant="body2">
                                  {camp?.name || campId}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{bounds.min ? `$${bounds.min}` : 'Global'}</TableCell>
                              <TableCell align="right">{bounds.max ? `$${bounds.max}` : 'Global'}</TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    const updated = { ...(editConfig.campaign_budget_overrides || {}) };
                                    delete updated[campId];
                                    set('campaign_budget_overrides', updated);
                                  }}
                                >
                                  Remove
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Add new override */}
                <Autocomplete
                  options={clientCampaigns.filter((c) => !(editConfig.campaign_budget_overrides || {})[c.id])}
                  getOptionLabel={(opt) => opt.name || opt.id}
                  value={clientCampaigns.find((c) => c.id === overrideCampaignId) || null}
                  onChange={(_, val) => setOverrideCampaignId(val?.id || '')}
                  loading={campaignsLoading}
                  sx={{ width: '100%' }}
                  componentsProps={{ popper: { style: { width: 'fit-content', maxWidth: '80vw' } } }}
                  renderInput={(params) => <TextField {...params} label="Select Campaign" size="small" />}
                />
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="Min Daily Budget ($)"
                    type="number"
                    size="small"
                    value={overrideMin}
                    onChange={(e) => setOverrideMin(e.target.value)}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Max Daily Budget ($)"
                    type="number"
                    size="small"
                    value={overrideMax}
                    onChange={(e) => setOverrideMax(e.target.value)}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="outlined"
                    disabled={!overrideCampaignId}
                    onClick={() => {
                      if (!overrideCampaignId) return;
                      const bounds = {};
                      if (overrideMin) bounds.min = Number(overrideMin);
                      if (overrideMax) bounds.max = Number(overrideMax);
                      set('campaign_budget_overrides', {
                        ...(editConfig.campaign_budget_overrides || {}),
                        [overrideCampaignId]: bounds,
                      });
                      setOverrideCampaignId('');
                      setOverrideMin('');
                      setOverrideMax('');
                    }}
                  >
                    Add Override
                  </Button>
                </Stack>

                <Box sx={{ pt: 1 }}>
                  <Button variant="contained" size="large" onClick={handleSave} disabled={saving} sx={{ minWidth: 200 }}>
                    {saving ? <CircularProgress size={22} /> : editConfig.id ? 'Save Changes' : 'Create Configuration'}
                  </Button>
                </Box>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}

// ── Main Page ───────────────────────────────────────────────────

function BudgetOptimizerPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);

  useEffect(() => {
    fetchClients()
      .then((res) => setClients(res.data || []))
      .catch(() => {})
      .finally(() => setClientsLoading(false));
  }, []);

  return (
    <Box>
      <Stack spacing={3}>
        <Card>
          <CardHeader
            title="Budget Optimizer"
            subheader="Automated campaign budget pacing and city-level bid optimization"
          />
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Optimizer Runs" />
            <Tab label="Settings" />
          </Tabs>
        </Card>

        {clientsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            {activeTab === 0 && <OptimizerRunsTab clients={clients} />}
            {activeTab === 1 && <OptimizerSettingsTab clients={clients} />}
          </>
        )}
      </Stack>
    </Box>
  );
}

export default BudgetOptimizerPage;
